import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Switch, Typography, Spin, message, Popconfirm, Tag, Tooltip, DatePicker } from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined,
    CopyOutlined,
    DownloadOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import DynamicForm from './DynamicForm';

const { Text } = Typography;
const { TextArea } = Input;

const BrandCodes = ({ brandId }) => {
    const [loading, setLoading] = useState(false);
    const [codes, setCodes] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [codeTypes, setCodeTypes] = useState([]);
    const [selectedCodeType, setSelectedCodeType] = useState(null);
    const [form] = Form.useForm();
    const [dataModalVisible, setDataModalVisible] = useState(false);
    const [selectedCodeData, setSelectedCodeData] = useState(null);
    const [fetchingData, setFetchingData] = useState(false);
    const [dataForm] = Form.useForm();

    // Kod tiplerini yükle
    const fetchCodeTypes = useCallback(async () => {
        try {
            const response = await api.get('/codetypes', {
                params: { includeInactive: false }
            });
            
            if (Array.isArray(response.data)) {
                setCodeTypes(response.data);
            } else {
                console.error('Beklenmeyen API yanıtı (Kod Tipleri):', response.data);
                message.error('Kod tipleri yüklenirken bir hata oluştu');
                setCodeTypes([]);
            }
        } catch (error) {
            console.error('Kod tipleri yükleme hatası:', error.response || error);
            message.error('Kod tipleri yüklenirken bir hata oluştu');
            setCodeTypes([]);
        }
    }, []);

    // Kodları yükle
    const fetchCodes = useCallback(async () => {
        if (!brandId) return;
        
        try {
            setLoading(true);
            const response = await api.get(`/brands/${brandId}/codes`, {
                params: { includeInactive: true }
            });
            
            if (Array.isArray(response.data)) {
                setCodes(response.data);
            } else {
                console.error('Beklenmeyen API yanıtı:', response.data);
                message.error('Marka kodları yüklenirken bir hata oluştu');
                setCodes([]);
            }
        } catch (error) {
            console.error('Marka kodları yükleme hatası:', error.response || error);
            message.error('Marka kodları yüklenirken bir hata oluştu');
            setCodes([]);
        } finally {
            setLoading(false);
        }
    }, [brandId]);

    // İlk yükleme
    useEffect(() => {
        fetchCodeTypes();
        fetchCodes();
    }, [fetchCodeTypes, fetchCodes]);

    // Kod tipi değiştiğinde
    const handleCodeTypeChange = (codeTypeId) => {
        const codeType = codeTypes.find(type => type.id === codeTypeId);
        setSelectedCodeType(codeType || null);
        
        // Form değerlerini temizle
        form.setFieldsValue({
            parameters: {},
        });
    };

    // Yeni kod ekle veya düzenle
    const handleSaveCode = async (values) => {
        console.log('Form values:', values);
        console.log('Parameters:', values.parameters);
        
        try {
            setLoading(true);
            
            const { codeTypeId, isActive, parameters } = values;
            
            // CodeType bilgisini al ve otomatik name oluştur
            const selectedCodeType = codeTypes.find(type => type.id === codeTypeId);
            const autoGeneratedName = selectedCodeType ? 
                `${selectedCodeType.name}_${brandId}_${Date.now()}` : 
                `Code_${brandId}_${Date.now()}`;
            
            if (editingCode) {
                // Mevcut kodu güncelle
                await api.put(`/brands/codes/${editingCode.id}`, {
                    codeTypeId,
                    name: editingCode.name || autoGeneratedName, // Mevcut name varsa kullan, yoksa otomatik oluştur
                    isActive,
                    parameters
                });
                message.success('Kod başarıyla güncellendi');
            } else {
                // Yeni kod oluştur
                await api.post(`/brands/${brandId}/codes`, {
                    codeTypeId,
                    name: autoGeneratedName, // Otomatik oluşturulan name
                    isActive,
                    parameters
                });
                message.success('Kod başarıyla eklendi');
            }
            
            // Modalı kapat, formu temizle ve kodları yeniden yükle
            setModalVisible(false);
            form.resetFields();
            setEditingCode(null);
            setSelectedCodeType(null);
            fetchCodes();
        } catch (error) {
            console.error('Kod kaydetme hatası:', error.response || error);
            message.error(error.response?.data?.error || 'Kod kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Kod sil
    const handleDeleteCode = async (id) => {
        try {
            setLoading(true);
            await api.delete(`/brands/codes/${id}`);
            message.success('Kod başarıyla silindi');
            fetchCodes();
        } catch (error) {
            console.error('Kod silme hatası:', error.response || error);
            message.error('Kod silinirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Düzenleme modalını aç
    const handleEdit = (record) => {
        setEditingCode(record);
        
        // Kod tipini bul ve seç
        const codeType = codeTypes.find(type => type.id === record.codeTypeId);
        setSelectedCodeType(codeType || null);
        
        form.setFieldsValue({
            codeTypeId: record.codeTypeId,
            parameters: record.parameters || {},
            isActive: record.isActive
        });
        setModalVisible(true);
    };

    // Panoya kopyala
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        message.success('Değer panoya kopyalandı');
    };

    // Veri çekme fonksiyonu
    const fetchCodeData = async (values) => {
        if (!selectedCodeData) return;
        
        try {
            setFetchingData(true);
            const { dateRange, apiKey, baseUrl } = values;
            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');
            
            // Kod tipini bul
            const codeType = codeTypes.find(type => type.id === selectedCodeData.codeTypeId);
            if (!codeType) {
                message.error('Kod tipi bulunamadı');
                return;
            }
            
            // Base URL'den son slash'i kaldır
            const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
            
            // API endpoint'ini oluştur
            const apiUrl = `${cleanBaseUrl}/api/${codeType.code}/${brandId}/${startDate}/${endDate}`;
            
            // Headers'ı hazırla
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Eğer API key varsa header'a ekle
            if (apiKey) {
                headers['x-api-key'] = apiKey;
            }
            
            // Veriyi çek
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: headers
            });
            const data = await response.json();
            
            if (response.ok) {
                // Başarılı - veriyi göster
                Modal.info({
                    title: `${codeType.name} Verileri`,
                    content: (
                        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                            <pre>{JSON.stringify(data, null, 2)}</pre>
                        </div>
                    ),
                    width: 800,
                    okText: 'Kapat'
                });
            } else {
                message.error(data.error || 'Veri çekilirken hata oluştu');
            }
        } catch (error) {
            console.error('Veri çekme hatası:', error);
            message.error('Veri çekilirken bir hata oluştu');
        } finally {
            setFetchingData(false);
            setDataModalVisible(false);
            dataForm.resetFields();
        }
    };

    // Veri çekme modalını aç
    const openDataModal = (record) => {
        setSelectedCodeData(record);
        dataForm.setFieldsValue({
            dateRange: [dayjs().subtract(7, 'days'), dayjs()]
        });
        setDataModalVisible(true);
    };

    // Parametre değerini formatla
    const formatParameterValue = (parameter, value) => {
        if (parameter.type === 'password') {
            return '••••••••••••';
        }
        return value;
    };

    // Parametre değerlerini göster
    const showParameters = (record) => {
        if (!record.parameters || Object.keys(record.parameters).length === 0) {
            return <Text type="secondary">Parametre yok</Text>;
        }

        const codeType = codeTypes.find(type => type.id === record.codeTypeId);
        if (!codeType || !Array.isArray(codeType.parameterSchema)) {
            return <Text type="secondary">Şema bulunamadı</Text>;
        }

        return (
            <div>
                {codeType.parameterSchema.map(param => {
                    const value = record.parameters[param.name];
                    if (value === undefined || value === null || value === '') {
                        return null;
                    }
                    
                    const displayValue = formatParameterValue(param, value);
                    
                    return (
                        <div key={param.name} style={{ marginBottom: 4 }}>
                            <Text strong>{param.label}: </Text>
                            <Space>
                                <Text>{displayValue}</Text>
                                {param.type !== 'password' && (
                                    <Button 
                                        icon={<CopyOutlined />} 
                                        size="small" 
                                        onClick={() => copyToClipboard(value)}
                                        title="Değeri Kopyala"
                                    />
                                )}
                            </Space>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Tablo sütunları
    const columns = [
        {
            title: 'Kod Tipi',
            dataIndex: 'CodeType',
            key: 'codeType',
            render: (codeType) => codeType ? codeType.name : '-',
            filters: codeTypes.map(type => ({ text: type.name, value: type.id })),
            onFilter: (value, record) => record.codeTypeId === value,
        },
        {
            title: 'Parametreler',
            key: 'parameters',
            render: (_, record) => showParameters(record),
            width: '40%',
        },
        {
            title: 'Durum',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Aktif' : 'Pasif'}
                </Tag>
            ),
            filters: [
                { text: 'Aktif', value: true },
                { text: 'Pasif', value: false },
            ],
            onFilter: (value, record) => record.isActive === value,
        },
        {
            title: 'İşlemler',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Düzenle">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Veri Çek">
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => openDataModal(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Bu kodu silmek istediğinize emin misiniz?"
                        onConfirm={() => handleDeleteCode(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingCode(null);
                        setSelectedCodeType(null);
                        form.resetFields();
                        form.setFieldsValue({
                            isActive: true,
                        });
                        setModalVisible(true);
                    }}
                    disabled={codeTypes.length === 0}
                >
                    Yeni Kod Ekle
                </Button>
                
                {codeTypes.length === 0 && (
                    <Text type="warning" style={{ marginLeft: 12 }}>
                        Kod tipi tanımlanmamış. Önce Kod Tipleri sayfasından kod tipleri tanımlayın.
                    </Text>
                )}
            </div>

            <Table
                columns={columns}
                dataSource={codes}
                rowKey="id"
                loading={loading}
                pagination={{
                    hideOnSinglePage: true,
                    pageSize: 10
                }}
                size="middle"
            />

            <Modal
                title={editingCode ? 'Kod Düzenle' : 'Yeni Kod Ekle'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingCode(null);
                    setSelectedCodeType(null);
                    form.resetFields();
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setModalVisible(false);
                        setEditingCode(null);
                        setSelectedCodeType(null);
                        form.resetFields();
                    }}>
                        İptal
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        onClick={() => form.submit()}
                        loading={loading}
                    >
                        Kaydet
                    </Button>
                ]}
                destroyOnClose
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSaveCode}
                    initialValues={{
                        isActive: true
                    }}
                >
                    <Form.Item
                        name="codeTypeId"
                        label="Kod Tipi"
                        rules={[{ required: true, message: 'Lütfen kod tipini seçin' }]}
                    >
                        <Select
                            placeholder="Kod tipi seçin"
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={handleCodeTypeChange}
                            disabled={!!editingCode}
                        >
                            {codeTypes.map(type => (
                                <Select.Option key={type.id} value={type.id}>
                                    {type.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedCodeType && (
                        <DynamicForm 
                            schema={selectedCodeType.parameterSchema}
                            codeType={selectedCodeType}
                        />
                    )}

                    <Form.Item
                        name="isActive"
                        label="Aktif"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Veri Çek"
                open={dataModalVisible}
                onCancel={() => {
                    setDataModalVisible(false);
                    setSelectedCodeData(null);
                    dataForm.resetFields();
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setDataModalVisible(false);
                        setSelectedCodeData(null);
                        dataForm.resetFields();
                    }}>
                        İptal
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        onClick={() => dataForm.submit()}
                        loading={fetchingData}
                    >
                        Veri Çek
                    </Button>
                ]}
                destroyOnClose
            >
                <Form
                    form={dataForm}
                    layout="vertical"
                    onFinish={fetchCodeData}
                >
                    <Form.Item
                        name="dateRange"
                        label="Tarih Aralığı"
                        rules={[{ required: true, message: 'Lütfen tarih aralığı seçin' }]}
                    >
                        <DatePicker.RangePicker 
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>
                    <Form.Item
                        name="baseUrl"
                        label="API Base URL"
                        rules={[{ required: true, message: 'API Base URL giriniz' }]}
                    >
                        <Input 
                            placeholder="https://api.domain.com"
                        />
                    </Form.Item>
                    <Form.Item
                        name="apiKey"
                        label="API Key (x-api-key)"
                        rules={[{ required: false, message: 'API Key giriniz' }]}
                    >
                        <Input.Password 
                            placeholder="API Key (opsiyonel)"
                            autoComplete="new-password"
                        />
                    </Form.Item>
                    {selectedCodeData && (
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">
                                {codeTypes.find(t => t.id === selectedCodeData.codeTypeId)?.name} verisi çekilecek
                            </Text>
                        </div>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default BrandCodes; 